import { useCallback, type MouseEvent } from "react";
import { Globe, Inbox, LogOut, Mailbox } from "lucide-react";

import type { PrincipalRole } from "~/domain/types";
import { useClient } from "~/app/client-context";
import { isMainNavActive, mainNavFor, userMenuFor } from "~/app/nav-model";
import { useNavigate } from "~/app/navigate-context";
import { useAppCollections } from "~/collections/app-db-context";
import { useCollectionArray } from "~/collections/use-collection-array";
import { accountLabel } from "~/lib/identity-label";
import { cn } from "~/lib/utils";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "~/components/ui/sidebar";

const ICONS = {
  inbox: Inbox,
  domains: Globe,
  mailboxes: Mailbox,
} as const;

/** Ember current item — DESIGN.md: ember is Compose, current nav, and focus. */
const EMBER_CURRENT = "bg-primary/10 font-medium text-primary data-[active=true]:bg-primary/10 data-[active=true]:text-primary";

export interface AppSidebarProps {
  pathname: string;
  role: PrincipalRole;
  userId: string;
  identitySubject?: string;
  dataMode?: "server" | "demo";
}

export function AppSidebar({ pathname, role, userId, identitySubject, dataMode = "server" }: AppSidebarProps) {
  const client = useClient();
  const navigate = useNavigate();
  const collections = useAppCollections();
  const { rows: mailboxes, loading: mailboxesLoading } = useCollectionArray(collections.mailboxes);
  const items = mainNavFor(role);
  const menu = userMenuFor(role);
  const label = accountLabel({ userId, identitySubject });
  const initials = label === "Account" ? "A" : label.slice(0, 2).toUpperCase();
  // In-flight collections still report [] — that is loading, not zero unread / no stuck.
  const unread = mailboxesLoading
    ? undefined
    : mailboxes.reduce((sum, mailbox) => sum + mailbox.unreadCount, 0);
  const stuck = mailboxesLoading
    ? undefined
    : mailboxes.filter(
        (mailbox) => mailbox.lifecycle === "provisioning" || mailbox.lifecycle === "failed",
      ).length;

  const go = useCallback(
    (href: string, event: MouseEvent<HTMLAnchorElement>) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      navigate(href);
    },
    [navigate],
  );

  const mailItems = items.filter((item) => item.group === "mail");
  const infrastructure = items.filter((item) => item.group === "infrastructure");

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <a href="/mail" onClick={(event) => go("/mail", event)}>
                <span className="flex size-8 items-center justify-center rounded-md bg-foreground text-xs font-bold text-background">
                  IF
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold">InboxFury</span>
                  {dataMode === "demo" ? (
                    <span className="text-xs text-sidebar-foreground/70">Preview data</span>
                  ) : null}
                </span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <nav aria-label="Primary navigation">
        <SidebarGroup>
          <SidebarGroupLabel>Mail</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mailItems.map((item) => {
                const Icon = ICONS[item.id];
                const current = isMainNavActive(item.href, pathname);
                const showUnread = item.id === "inbox" && unread !== undefined && unread > 0;
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={current}
                      tooltip={item.title}
                      className={cn(current && EMBER_CURRENT)}
                    >
                      <a
                        href={item.href}
                        aria-current={current ? "page" : undefined}
                        aria-describedby={showUnread ? "sidebar-inbox-unread" : undefined}
                        onClick={(event) => go(item.href, event)}
                      >
                        <Icon />
                        <span>{item.title}</span>
                      </a>
                    </SidebarMenuButton>
                    {showUnread ? (
                      <SidebarMenuBadge id="sidebar-inbox-unread">
                        <span className="sr-only">{unread} unread. </span>
                        {unread > 99 ? "99+" : unread}
                      </SidebarMenuBadge>
                    ) : null}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {infrastructure.length > 0 ? (
          <SidebarGroup>
            <SidebarGroupLabel>Infrastructure</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {infrastructure.map((item) => {
                  const Icon = ICONS[item.id];
                  const current = isMainNavActive(item.href, pathname);
                  const badge = item.id === "mailboxes" && stuck !== undefined && stuck > 0 ? stuck : 0;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        isActive={current}
                        tooltip={item.title}
                        className={cn(current && EMBER_CURRENT)}
                      >
                        <a
                          href={item.href}
                          aria-current={current ? "page" : undefined}
                          onClick={(event) => go(item.href, event)}
                        >
                          <Icon />
                          <span>{item.title}</span>
                        </a>
                      </SidebarMenuButton>
                      {badge > 0 ? (
                        <SidebarMenuBadge>
                          <span className="sr-only">{badge} stuck provisioning. </span>
                          {badge > 99 ? "99+" : badge}
                        </SidebarMenuBadge>
                      ) : null}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
        </nav>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent"
                  aria-label="Account menu"
                >
                  <Avatar className="size-8">
                    <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="flex min-w-0 flex-1 flex-col text-left leading-tight">
                    <span className="truncate text-sm font-medium">{label}</span>
                    <span className="truncate text-xs text-sidebar-foreground/70">{role}</span>
                  </span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" side="top" align="start">
                <DropdownMenuLabel>Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {menu.map((item) => (
                  <DropdownMenuItem
                    key={item.id}
                    onSelect={() => navigate(item.href)}
                  >
                    {item.title}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => {
                    void client.signOut().finally(() => {
                      window.location.assign(dataMode === "demo" ? "/demo.html" : "/signin");
                    });
                  }}
                >
                  <LogOut />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

/** Kept so absence tests can still name the Engage surfaces that must not return. */
export function buildAppSidebarSections(_isAdmin: boolean) {
  return [
    { label: "Mail", items: [{ title: "Inbox", to: "/mail" }] },
    { label: "Infrastructure", items: [{ title: "Domains", to: "/domains" }, { title: "Mailboxes", to: "/mailboxes" }] },
  ];
}

export async function fetchSession(): Promise<null> {
  return null;
}
